import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { FileUpload, FileUploadSelectEvent } from 'primereact/fileupload';
import { Image } from 'primereact/image';
import { Banner, CreateBannerDto, UpdateBannerDto } from '../../../types/banner.types';
import { SystemImageUploadService } from '../../../service/supabase/SystemImageUploadService';

interface BannerDialogProps {
    visible: boolean;
    banner: Banner | null;
    onHide: () => void;
    onSave: (banner: CreateBannerDto | UpdateBannerDto) => Promise<void>;
    isLoading?: boolean;
    title?: string;
}

export const BannerDialog: React.FC<BannerDialogProps> = ({
    visible,
    banner,
    onHide,
    onSave,
    isLoading = false,
    title = 'Chi tiết Banner'
}) => {
    const [formData, setFormData] = useState<CreateBannerDto | UpdateBannerDto>({
        url_image: '',
        url_banner: '',
        index: undefined
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const fileUploadImageRef = useRef<any>(null);
    const fileUploadBannerRef = useRef<any>(null);

    // Reset form when modal opens/closes or banner changes
    useEffect(() => {
        if (visible && banner) {
            setFormData({
                url_image: banner.url_image,
                url_banner: banner.url_banner,
                index: banner.index
            });
        } else if (visible && !banner) {
            setFormData({
                url_image: '',
                url_banner: '',
                index: undefined
            });
        }

        setErrors({});
    }, [visible, banner]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for the field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleNumberChange = (e: { value: number | null }) => {
        setFormData(prev => ({
            ...prev,
            index: e.value === null ? undefined : e.value
        }));

        // Clear error for the field
        if (errors.index) {
            setErrors(prev => ({
                ...prev,
                index: ''
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.url_image || formData.url_image.trim() === '') {
            newErrors.url_image = 'Vui lòng nhập đường dẫn hình ảnh';
        }

        if (!formData.url_banner || formData.url_banner.trim() === '') {
            newErrors.url_banner = 'Vui lòng nhập đường dẫn liên kết';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            try {
                await onSave(formData);
                onHide();
            } catch (error) {
                console.error('Error saving banner:', error);
            }
        }
    };

    const handleImageSelect = async (e: FileUploadSelectEvent) => {
        const file = e.files[0];
        if (!file) return;

        setUploadingImage(true);
        const { url, error } = await SystemImageUploadService.uploadBannerImage(file);
        setUploadingImage(false);

        if (error) {
            setErrors(prev => ({ ...prev, url_image: error }));
        } else if (url) {
            setFormData(prev => ({ ...prev, url_image: url }));
            if (errors.url_image) {
                setErrors(prev => ({ ...prev, url_image: '' }));
            }
        }

        // Clear the file upload
        if (fileUploadImageRef.current) {
            fileUploadImageRef.current.clear();
        }
    };

    const handleBannerSelect = async (e: FileUploadSelectEvent) => {
        const file = e.files[0];
        if (!file) return;

        setUploadingBanner(true);
        const { url, error } = await SystemImageUploadService.uploadBannerImage(file);
        setUploadingBanner(false);

        if (error) {
            setErrors(prev => ({ ...prev, url_banner: error }));
        } else if (url) {
            setFormData(prev => ({ ...prev, url_banner: url }));
            if (errors.url_banner) {
                setErrors(prev => ({ ...prev, url_banner: '' }));
            }
        }

        // Clear the file upload
        if (fileUploadBannerRef.current) {
            fileUploadBannerRef.current.clear();
        }
    };

    const renderFooter = () => {
        return (
            <div>
                <Button
                    label="Hủy"
                    icon="pi pi-times"
                    onClick={onHide}
                    className="p-button-text"
                    disabled={isLoading}
                />
                <Button
                    label={banner ? 'Cập nhật' : 'Thêm mới'}
                    icon="pi pi-check"
                    onClick={handleSubmit}
                    className="p-button-primary"
                    loading={isLoading}
                />
            </div>
        );
    };

    return (
        <Dialog
            header={title}
            visible={visible}
            style={{ width: '500px' }}
            onHide={onHide}
            modal
            footer={renderFooter()}
            closeOnEscape={!isLoading}
            closable={!isLoading}
            className="p-fluid"
        >
            {/* Title and content fields removed */}

            <div className="field">
                <label htmlFor="url_image" className="font-bold">URL Hình ảnh *</label>
                <div className="flex gap-2 align-items-center mb-2">
                    <FileUpload
                        ref={fileUploadImageRef}
                        mode="basic"
                        name="image"
                        accept="image/*"
                        maxFileSize={5000000}
                        onSelect={handleImageSelect}
                        auto={false}
                        chooseLabel="Chọn ảnh"
                        disabled={isLoading || uploadingImage}
                        className="flex-1"
                    />
                    {uploadingImage && <i className="pi pi-spin pi-spinner" />}
                </div>
                <InputText
                    id="url_image"
                    name="url_image"
                    value={formData.url_image || ''}
                    onChange={handleChange}
                    className={errors.url_image ? 'p-invalid' : ''}
                    disabled={isLoading}
                    placeholder="Hoặc nhập URL trực tiếp"
                />
                {errors.url_image && <small className="p-error">{errors.url_image}</small>}
                {formData.url_image && (
                    <div className="mt-2">
                        <Image src={formData.url_image} alt="Preview" width="200" preview />
                    </div>
                )}
            </div>

            <div className="field">
                <label htmlFor="url_banner" className="font-bold">URL Liên kết (Bài viết) *</label>

                <InputText
                    id="url_banner"
                    name="url_banner"
                    value={formData.url_banner || ''}
                    onChange={handleChange}
                    className={errors.url_banner ? 'p-invalid' : ''}
                    disabled={isLoading}
                    placeholder="Hoặc nhập URL trực tiếp"
                />
                {errors.url_banner && <small className="p-error">{errors.url_banner}</small>}
                {formData.url_banner && (
                    <div className="mt-2">
                        <Image src={formData.url_banner} alt="Preview" width="200" preview />
                    </div>
                )}
            </div>

            <div className="field">
                <label htmlFor="index" className="font-bold">Thứ tự hiển thị</label>
                <InputNumber
                    id="index"
                    value={formData.index}
                    onChange={handleNumberChange}
                    min={0}
                    disabled={isLoading}
                />
                <small className="text-muted">Để trống để tự động sắp xếp</small>
            </div>
        </Dialog>
    );
};
